import React from "react";
import { Link } from "react-router-dom";
import { Seo } from "../../../components/Seo";

type Section = {
  title: string;
  description: string;
  capabilities: string[];
};

const sections: Section[] = [
  {
    title: "Dashboard and Analytics",
    description:
      "A central workspace for operational and business visibility across orders, customers, inventory, and reporting.",
    capabilities: [
      "Business dashboard and analytics views",
      "Order, customer, and inventory snapshots",
      "Reporting center and quick navigation to key KPIs",
    ],
  },
  {
    title: "Orders, Jobs, and Quality",
    description:
      "Tools to manage customer orders, track job execution, and maintain quality checks throughout fulfillment.",
    capabilities: [
      "Customer orders and quotations",
      "Job orders and production tracking",
      "Quality tracking, corrective actions, and quality reporting",
    ],
  },
  {
    title: "Purchasing and Vendor Operations",
    description:
      "End-to-end purchasing capabilities from supplier onboarding to receiving and invoice processing.",
    capabilities: [
      "Vendor master, vendor orders, and vendor inbox",
      "Vendor receiving and vendor quotations",
      "Invoice list, approval, and payment workflows",
      "AI-powered extraction from invoice PDFs and vendor email attachments into forms",
    ],
  },
  {
    title: "Accounting and Finance",
    description:
      "Accounting workflows to control receivables, journals, reconciliation, and period-end operations.",
    capabilities: [
      "Receivables, invoices, draft inbox, and register",
      "Journals, reconciliation, and accounting periods",
      "Bank sync, chart of accounts, and payroll import support",
    ],
  },
  {
    title: "Masters and System Setup",
    description:
      "Foundational master data and configuration that drive consistency across operational and financial workflows.",
    capabilities: [
      "Customer, vendor, product, process, and location masters",
      "Bank, credit card, and document masters",
      "Part templates, workstation, and price breakdown setup",
    ],
  },
  {
    title: "Administration and Security",
    description:
      "Administrative controls for user management and controlled access to platform modules.",
    capabilities: [
      "User management and role permissions",
      "Module access based on configured roles",
      "Centralized administration for secure operations",
    ],
  },
];

const CimmpleFlowDocsOverview: React.FC = () => {
  return (
    <div className="pt-24">
      <Seo
        title="CimmpleFlow Docs | Cimmple"
        description="Overview of CimmpleFlow—a modern cloud operating system for machine shops that includes manufacturing ERP capabilities for operations, finance, purchasing, and reporting."
      />

      <section className="pt-8 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold text-primary-700 dark:text-primary-400 uppercase tracking-wide mb-3">
            CimmpleFlow Docs
          </p>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-4 text-balance">
            CimmpleFlow Overview
          </h1>
          <p className="text-lg text-slate-700 dark:text-dark-300 max-w-4xl text-balance">
            This page summarizes what CimmpleFlow can do as a cloud operating system for machine shops—useful if you are
            comparing solutions, onboarding a team, or explaining the product to colleagues. It is not a substitute for
            hands-on training in your own account.
          </p>
        </div>
      </section>

      <section className="pb-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card p-6 md:p-8">
            <h2 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-3">What you will find here</h2>
            <ul className="list-disc pl-5 space-y-2 text-slate-700 dark:text-dark-300">
              <li>A plain-language map of major areas: operations, purchasing, accounting, setup, and reporting.</li>
              <li>Typical workflows and capabilities—without assuming prior ERP experience.</li>
              <li>Pointers to your Cimmple team or in-product resources when you need detailed, account-specific steps.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="pb-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sections.map((section) => (
              <article key={section.title} className="card p-6 h-full">
                <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-2">{section.title}</h3>
                <p className="text-slate-700 dark:text-dark-300 mb-4">{section.description}</p>
                <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-700 dark:text-dark-300">
                  {section.capabilities.map((capability) => (
                    <li key={capability}>{capability}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card p-6 md:p-8">
            <h2 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-3">Need detailed how-tos?</h2>
            <p className="text-slate-700 dark:text-dark-300 mb-4">
              Screen-by-screen guides, company-specific setup, and training are best delivered inside your CimmpleFlow
              environment or with your Cimmple contact—so instructions match your roles, locations, and go-live plan.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary">
                Contact Cimmple
              </Link>
              <Link to="/docs" className="btn-secondary">
                Back to Docs Hub
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CimmpleFlowDocsOverview;
